/**
 * UTM Tracker - Captures and stores UTM parameters
 *
 * @package AdvancedTrackingManager
 * @since 1.0.0
 */

import { onReady } from '@lib/dom';
import { createLogger } from '@lib/logger';
import { setCookie, getCookie } from '@lib/cookies';
import { ensureDataLayer } from '@lib/data-layer';

const w = globalThis as unknown as Window;

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const getUTMParams = (): Record<string, string> => {
  const urlParams = new URLSearchParams(w.location.search);
  const utmParams: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const v = urlParams.get(key);
    if (v) utmParams[key] = v;
  }
  return utmParams;
};

const storeUTMParams = (
  utmParams: Record<string, string>,
  cookieDuration: number,
  log: ReturnType<typeof createLogger>
): void => {
  if (Object.keys(utmParams).length === 0) return;
  for (const key of Object.keys(utmParams)) {
    const cookieName = `atm_${key}`;
    setCookie(cookieName, utmParams[key]!, cookieDuration);
    log.debug('Stored UTM parameter', { key, value: utmParams[key] });
  }
  setCookie('atm_utm_params', JSON.stringify(utmParams), cookieDuration);
  log.debug('Stored UTM parameters', utmParams);
};

const getStoredUTMParams = (log: ReturnType<typeof createLogger>): Record<string, string> => {
  const stored = getCookie('atm_utm_params');
  if (stored) {
    try {
      return JSON.parse(stored) as Record<string, string>;
    } catch (e) {
      log.debug('Error parsing stored UTM params', e);
      return {};
    }
  }
  const utmParams: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = getCookie(`atm_${key}`);
    if (value) utmParams[key] = value;
  }
  return utmParams;
};

const sendToDataLayer = (
  utmParams: Record<string, string>,
  eventName: string,
  log: ReturnType<typeof createLogger>
): void => {
  ensureDataLayer();
  const eventData = {
    event: eventName || 'utm_tracked',
    utm_source: utmParams.utm_source ?? '',
    utm_medium: utmParams.utm_medium ?? '',
    utm_campaign: utmParams.utm_campaign ?? '',
    utm_content: utmParams.utm_content ?? '',
    utm_term: utmParams.utm_term ?? '',
  };
  w.dataLayer!.push(eventData);
  log.debug('Sent UTM params to dataLayer', eventData);
};

export const utmTracker = {
  getStored: (): Record<string, string> =>
    getStoredUTMParams(createLogger('ATM UTM', (w.atmConfig?.debugMode ?? false))),
  getCurrent: getUTMParams,
  sendToDataLayer: (): void => {
    const stored = getStoredUTMParams(createLogger('ATM UTM', (w.atmConfig?.debugMode ?? false)));
    sendToDataLayer(stored, 'page_view', createLogger('ATM UTM', (w.atmConfig?.debugMode ?? false)));
  },
};

const init = (): void => {
  if (typeof w.atmConfig === 'undefined') return;

  const config = w.atmConfig;
  const debugMode = config.debugMode ?? false;
  const cookieDuration = config.utmCookieDuration ?? 31536000;
  const log = createLogger('ATM UTM', debugMode);

  log.debug('Initializing UTM tracker');

  const urlUTMs = getUTMParams();
  const storedUTMs = getStoredUTMParams(log);

  if (Object.keys(urlUTMs).length > 0) {
    storeUTMParams(urlUTMs, cookieDuration, log);
    sendToDataLayer(urlUTMs, 'utm_captured', log);
  } else if (Object.keys(storedUTMs).length > 0) {
    sendToDataLayer(storedUTMs, 'page_view', log);
  }

  w.atmUTMTracker = {
    getStored: () => getStoredUTMParams(log),
    getCurrent: getUTMParams,
    sendToDataLayer: () => sendToDataLayer(getStoredUTMParams(log), 'page_view', log),
  };
};

onReady(init);
